const dateOptions = {
  year: 'numeric',
  month: 'long',
  day: 'numeric'
};

const timeOptions = {
  hour: 'numeric',
  minute: 'numeric'
};

const weekdayOptions = {
  weekday: 'long'
};

const safeLocalise = (fn) => (locale, date) => {
  if (isNaN(date)) {
    return null;
  }
  try {
    return fn(locale, date);
  } catch (err) {
    return null;
  }
};

/**
 * resetToUTC `applies` the timezone offset in the provided Date
 * updating the time to compensate for the offset and returning a new "local" Date object.
 *
 * This is particularly useful when rendering Dates from other timezones,
 * which have to be displayed as if the user was in the given timezone.
 *
 * For instance, showing 5AM in São Paulo to a user in Germany, we still want to show it as 5AM,
 * rather than 10AM (which is the corresponding local time in Berlin).
 *
 * @param {Date} original original Date (will not be mutated)
 * @returns new Date object with updated time
 */
export const resetToUTC = (original) => {
  const date = new Date(original);
  date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
  return date;
};

/**
 * localiseDate safely formats the given Date object using the provided locale and the default
 * date formatting options
 *
 * @param {string} locale user's locale in the lang-region format, e.g. `en-gb` or `sk-sk`
 * @param {Date} date Date object to format
 * @returns {string|null} null if the Date or locale is invalid, string otherwise
 */
export const localiseDate = safeLocalise((locale, date) => (
  date.toLocaleDateString(locale, dateOptions)
));

/**
 * localiseTime safely formats the given Date object using the provided locale and options
 * to display hours and minutes
 *
 * @param {string} locale user's locale in the lang-region format, e.g. `en-gb` or `sk-sk`
 * @param {Date} date Date object to format
 * @returns {string|null} null if the Date or locale is invalid, string otherwise
 */
export const localiseTime = safeLocalise((locale, date) => (
  date.toLocaleTimeString(locale, timeOptions)
));

const relativeFormatterMap = new Map();

const defaultRelativeFormatterOptions = {
  numeric: 'auto',
  style: 'long',
};

// retrieves a memoized formatter to reduce memory usage
const getRelativeFormatterForLocale = (locale, options = {}) => {
  if (relativeFormatterMap.has(locale)) {
    return relativeFormatterMap.get(locale);
  }

  const formatter = new Intl.RelativeTimeFormat(
    locale,
    { ...defaultRelativeFormatterOptions, ...options },
  );
  relativeFormatterMap.set(locale, formatter);
  return formatter;
};

const relativeDateDivisions = [
  { amount: 60, name: 'seconds' },
  { amount: 60, name: 'minutes' },
  { amount: 24, name: 'hours' },
  { amount: 7, name: 'days' },
  { amount: 4.34524, name: 'weeks' },
  { amount: 12, name: 'months' },
  { amount: Number.POSITIVE_INFINITY, name: 'years' },
];

const millisecondsToSeconds = (milliseconds) => milliseconds / 1000;

export const localiseWeekday = (locale, index) => {
  if (index == null) {
    return null;
  }
  const day = 2;
  const date = new Date(Date.UTC(2017, 0, day)); // Monday
  date.setDate(day + index);
  const localiseFn = (lcl, dt) => dt.toLocaleDateString(lcl, weekdayOptions);
  return safeLocalise(localiseFn)(locale, date);
};

/**
 * getDuration returns the difference between the provided date and now, in milliseconds
 *
 * @param {Date} date first date
 * @returns
 */
export const getDurationFromNow = (date) => date - new Date();

/**
 * localiseTimeFromNow calculates the time between the provided Date and current time,
 * and returns a localised relative time string.
 *
 * @param {string} locale user's locale in the lang-region format, e.g. `en-gb` or `sk-sk`
 * @param {*} date date in the past, which will be compared to current date
 * @returns {string|null} null if the Date or locale is invalid, string otherwise
 */
export const localiseTimeFromNow = (locale, date) => {
  if (!global.Intl?.RelativeTimeFormat) {
    return null;
  }

  try {
    const formatter = getRelativeFormatterForLocale(locale);
    const durationMilliseconds = getDurationFromNow(date);
    let duration = millisecondsToSeconds(durationMilliseconds);

    for (const division of relativeDateDivisions) {
      if (Math.abs(duration) < division.amount) {
        return formatter.format(Math.round(duration), division.name);
      }
      duration /= division.amount;
    }
  } catch (err) {
    return null;
  }

  return null;
};
